import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Textarea } from '@/components/ui-kit/textarea';
import { Label } from '@/components/ui-kit/label';
import { QuestionEditor } from '../../components/question-editor/question-editor';
import {
  useCreateSurveyForm,
  useGetSurveyFormById,
  useUpdateSurveyForm,
} from '../../hooks/use-survey-forms';
import { Question, SurveyForm, SurveyFormsPagingResult } from '../../types/survey-form.types';
import { FormPageSkeleton, PageHeader } from '@/components/core';

export const SurveyFormEditorPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formId } = useParams<{ formId: string }>();
  const isEditMode = Boolean(formId) && formId !== 'new';

  const { data, isLoading } = useGetSurveyFormById(isEditMode ? formId : undefined);
  const existingForm: SurveyForm | undefined = (data as { SurveyForms?: SurveyFormsPagingResult })
    ?.SurveyForms?.items?.[0];

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);

  useEffect(() => {
    if (!isEditMode || !existingForm) return;
    setTitle(existingForm.Title);
    setDescription(existingForm.Description ?? '');
    try {
      setQuestions(JSON.parse(existingForm.QuestionsJson) as Question[]);
    } catch {
      setQuestions([]);
    }
  }, [isEditMode, existingForm]);

  const { mutateAsync: createSurveyForm, isPending: isCreating } = useCreateSurveyForm();
  const { mutateAsync: updateSurveyForm, isPending: isUpdating } = useUpdateSurveyForm();
  const isSubmitting = isCreating || isUpdating;

  const handleSave = async () => {
    const questionsJson = JSON.stringify(questions);
    try {
      if (isEditMode && existingForm) {
        // Intentionally omit HasSections/SectionsJson on update: this editor
        // only authors flat questions, so leaving those fields out preserves
        // whatever section data already exists on the record instead of
        // clobbering it.
        await updateSurveyForm({
          itemId: existingForm.ItemId,
          input: { Title: title, Description: description, QuestionsJson: questionsJson },
        });
      } else {
        // New forms created here are always flat (unsectioned).
        await createSurveyForm({
          Title: title,
          Description: description,
          QuestionsJson: questionsJson,
          HasSections: false,
        });
      }
      navigate('/hr/survey-form-creation');
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  if (isEditMode && isLoading) return <FormPageSkeleton fields={6} />;

  return (
    <div className="flex w-full flex-col gap-6">
      <PageHeader title={isEditMode ? t('EDIT_SURVEY_FORM') : t('CREATE_SURVEY_FORM')} />

      <div className="space-y-1.5">
        <Label>{t('TITLE')}</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('TITLE')} />
      </div>

      <div className="space-y-1.5">
        <Label>{t('DESCRIPTION')}</Label>
        <Textarea
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('DESCRIPTION')}
        />
      </div>

      <div>
        <h2 className="mb-3 text-base font-semibold">{t('QUESTIONS')}</h2>
        <QuestionEditor questions={questions} onChange={setQuestions} />
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/hr/survey-form-creation')}
        >
          {t('CANCEL')}
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          loading={isSubmitting}
          disabled={isSubmitting || !title}
        >
          {isEditMode ? t('SAVE_CHANGES') : t('CREATE')}
        </Button>
      </div>
    </div>
  );
};
